import { RightOutlined, TeamOutlined } from "@ant-design/icons"
import { Checkbox } from "antd"
import React, { memo } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import {
  type AdditionalGroup,
  type AdditionalGroupKey,
  type PermissionRef,
  type SectionLayout,
  getGroupDisclosureId,
  isVisibilityKey
} from "../../helpers/layout.ts"
import { getDisplayEntry } from "../../helpers/utils.ts"
import type {
  OnBulkChange,
  OnFeaturesToggle,
  PermissionsFlat,
  SectionRecord
} from "../../types.ts"
import type { PermissionFeatures } from "../../types/permissions.types.ts"
import { PermissionCheckbox } from "./PermissionCheckbox.tsx"
import { PermissionsMatrix } from "./PermissionsMatrix.tsx"
import { usePermissionsEditor } from "./editor-context.ts"
import {
  byStoredRow,
  useWatchedPermissions
} from "./use-watched-permissions.ts"

const GROUP_TITLE_KEYS: Record<AdditionalGroupKey, string> = {
  files: "permissions.editor.groupFiles",
  locations: "permissions.editor.groupLocations",
  signatures: "permissions.editor.groupSignatures",
  approvals: "permissions.editor.groupApprovals",
  team: "permissions.editor.groupTeam",
  financial: "permissions.editor.groupFinancial",
  lifecycle: "permissions.editor.groupLifecycle",
  advanced: "permissions.editor.groupAdvanced"
}

interface PermissionsSectionPanelProps {
  layout: SectionLayout
  sections: PermissionsFlat
  roleName?: string
  roleUserCount?: number
  headerAction?: React.ReactNode
  onBulkChange: OnBulkChange
  onFeaturesToggle?: OnFeaturesToggle
}

const Card = ({
  title,
  description,
  action,
  children
}: {
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
}) => (
  <section className="rounded-xl border border-border bg-white p-4 sm:p-5">
    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="m-0 text-left text-base font-semibold text-gray-900">
          {title}
        </h3>
        {description && (
          <p className="m-0 mt-0.5 text-[13px] text-gray-500">{description}</p>
        )}
      </div>
      {action}
    </div>
    {children}
  </section>
)

const storedValue = (sections: PermissionsFlat, ref: PermissionRef) =>
  (sections as Record<string, SectionRecord | undefined>)[ref.sectionKey]?.[
    ref.subKey
  ]?.[ref.actionKey] ?? false

const LabelledPermission = ({
  permission,
  sections
}: {
  permission: PermissionRef
  sections: PermissionsFlat
}) => {
  const { displayMap } = usePermissionsEditor()
  const tooltip = getDisplayEntry(displayMap, permission.actionKey)?.tooltip
  return (
    <div className="flex flex-col gap-0.5 py-1">
      <PermissionCheckbox
        sectionKey={permission.sectionKey}
        subKey={permission.subKey}
        actionKey={permission.actionKey}
        initialValue={storedValue(sections, permission)}
        describeInTooltip={!tooltip}
      >
        <span className="text-gray-900">{permission.label}</span>
      </PermissionCheckbox>
      {tooltip && <span className="pl-6 text-xs text-gray-500">{tooltip}</span>}
    </div>
  )
}

const AdditionalGroupView = ({
  sectionKey,
  group,
  sections,
  defaultOpen
}: {
  sectionKey: string
  group: AdditionalGroup
  sections: PermissionsFlat
  defaultOpen: boolean
}) => {
  const { t } = useTranslation()
  const { isOpen, setOpen } = usePermissionsEditor()
  const disclosureId = getGroupDisclosureId(sectionKey, group.key)
  const open = isOpen(disclosureId, defaultOpen)
  const panelId = `${disclosureId}-panel`
  const enabled = useWatchedPermissions(group.items, sections).filter(
    Boolean
  ).length

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(disclosureId, !open)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
      >
        <RightOutlined
          aria-hidden
          className={cn(
            "text-[11px] text-gray-500 transition-transform",
            open && "rotate-90"
          )}
        />
        <span className="flex-1 font-medium text-gray-900">
          {t(GROUP_TITLE_KEYS[group.key])}
        </span>
        {!open && enabled > 0 && (
          <span className="text-xs text-gray-500">
            {t("permissions.editor.enabledCount", { count: enabled })}
          </span>
        )}
      </button>
      {/* Kept mounted while collapsed so its values stay registered and saved. */}
      <div id={panelId} hidden={!open} className="px-4 pb-3 pl-11">
        <div className="grid gap-x-8 gap-y-1 md:grid-cols-2">
          {group.items.map((ref) => (
            <LabelledPermission
              key={ref.actionKey}
              permission={ref}
              sections={sections}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const SelectAllAdditional = ({
  sectionTitle,
  sections,
  permissions,
  onFeaturesToggle
}: {
  sectionTitle: string
  sections: PermissionsFlat
  permissions: PermissionRef[]
  onFeaturesToggle: OnFeaturesToggle
}) => {
  const { t } = useTranslation()
  const { disabled, isUpdating } = usePermissionsEditor()
  const values = useWatchedPermissions(permissions, sections)
  const checked = values.length > 0 && values.every(Boolean)
  const includesVisibility = permissions.some((ref) =>
    isVisibilityKey(ref.actionKey)
  )

  const toggle = (value: boolean) =>
    byStoredRow(permissions).forEach(({ sectionKey, subKey, items }) =>
      onFeaturesToggle(
        sectionKey as keyof PermissionFeatures,
        Object.fromEntries(
          items.map((ref) => [ref.actionKey, storedValue(sections, ref)])
        ),
        value,
        subKey
      )
    )

  return (
    <Checkbox
      checked={checked}
      indeterminate={!checked && values.some(Boolean)}
      disabled={disabled || isUpdating}
      onChange={(e) => toggle(e.target.checked)}
    >
      <span className="text-[13px] text-gray-600">
        {includesVisibility
          ? t("permissions.editor.selectAllAdditionalWithVisibility", {
              section: sectionTitle
            })
          : t("permissions.editor.selectAllAdditional", {
              section: sectionTitle
            })}
      </span>
    </Checkbox>
  )
}

export const PermissionsSectionPanel = memo(function PermissionsSectionPanel({
  layout,
  sections,
  roleName,
  roleUserCount,
  headerAction,
  onBulkChange,
  onFeaturesToggle
}: PermissionsSectionPanelProps) {
  const { t } = useTranslation()
  const selectAllFeatures = layout.bulkFeatures.length > 0 &&
    onFeaturesToggle && (
      <SelectAllAdditional
        sectionTitle={layout.title}
        sections={sections}
        permissions={layout.bulkFeatures}
        onFeaturesToggle={onFeaturesToggle}
      />
    )

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="m-0 text-left text-2xl font-semibold text-gray-900">
            {layout.title}
          </h2>
          {roleName && (
            <p className="m-0 mt-1 text-sm text-gray-500">
              {t("permissions.editor.sectionSubtitle", { role: roleName })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {typeof roleUserCount === "number" && (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-gray-600">
              <TeamOutlined aria-hidden />
              {t("permissions.editor.teamMembers", { count: roleUserCount })}
            </span>
          )}
          {headerAction}
        </div>
      </header>

      {layout.visibility.length > 0 && (
        <Card
          title={t("permissions.editor.recordVisibility")}
          description={t("permissions.editor.recordVisibilityScope")}
          action={layout.additional.length === 0 && selectAllFeatures}
        >
          <div className="flex flex-col gap-1">
            {layout.visibility.map((ref) => (
              <LabelledPermission
                key={ref.actionKey}
                permission={ref}
                sections={sections}
              />
            ))}
          </div>
        </Card>
      )}

      {layout.rows.length > 0 && (
        <Card title={t("permissions.editor.mainActions")}>
          <PermissionsMatrix
            sectionKey={layout.key}
            sectionTitle={layout.title}
            sections={sections}
            columns={layout.columns}
            rows={layout.rows}
            inherited={layout.inherited}
            onBulkChange={onBulkChange}
          />
        </Card>
      )}

      {layout.actions.length > 0 && (
        <Card title={t("permissions.editor.actions")}>
          <div className="grid gap-x-8 gap-y-1 md:grid-cols-2">
            {layout.actions.map((ref) => (
              <LabelledPermission
                key={ref.actionKey}
                permission={ref}
                sections={sections}
              />
            ))}
          </div>
        </Card>
      )}

      {layout.additional.length > 0 && (
        <Card
          title={t("permissions.editor.additionalPermissions")}
          action={selectAllFeatures}
        >
          <div className="rounded-lg border border-border">
            {layout.additional.map((group, index) => (
              <AdditionalGroupView
                key={group.key}
                sectionKey={layout.key}
                group={group}
                sections={sections}
                defaultOpen={index === 0}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  )
})
